import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Alert } from '@kcm/ui'
import { AuthLayout, FunnelLayout } from './components/FunnelLayout'
import { ConsentBanner } from './components/ConsentBanner'
import { I18nProvider } from './components/I18nProvider'
import { SiteProvider } from './components/SiteProvider'
import { captureAttribution } from './lib/attribution'
import { applyBrand, applyCustomHead, fetchSiteConfig } from './lib/config'
import { type Consent, readConsent, tagsAllowed, writeConsent } from './lib/consent'
import { ConfirmDetails } from './screens/ConfirmDetails'
import { ForgotPassword } from './screens/ForgotPassword'
import { Login } from './screens/Login'
import { StepAccount } from './screens/StepAccount'
import { StepCompliance } from './screens/StepCompliance'
import { StepDone } from './screens/StepDone'
import { StepIdentity } from './screens/StepIdentity'
import { VerifyReturn } from './screens/VerifyReturn'

/**
 * The funnel shell (PLAN.md §4.5, §13).
 *
 * Two things happen before any step renders, in this order:
 *
 *   1. The site config is fetched. The bundle is domain-agnostic by design, so
 *      until this lands the app does not know its own name, brand or locales.
 *   2. The landing is recorded, with whatever `lid` / `pid` the URL carried.
 *      This runs before the user touches anything, because the largest share of
 *      abandonment happens on the first screen (§1.1) and those visits still
 *      need to be counted.
 */
export function App() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['site-config'],
    queryFn: ({ signal }) => fetchSiteConfig(signal),
    retry: 1,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (data) applyBrand(data)
  }, [data])

  const [consent, setConsent] = useState<Consent | null>(() => readConsent())

  // Tracking tags load only once the visitor has consented (§19).
  useEffect(() => {
    if (data && tagsAllowed(consent)) applyCustomHead(data.customHead)
  }, [data, consent])

  useEffect(() => {
    void captureAttribution()
  }, [])

  if (isPending) {
    return (
      <main className="grid min-h-dvh place-items-center p-6">
        {/* No brand tokens loaded yet, so this stays deliberately neutral. */}
        <div className="h-8 w-40 animate-pulse rounded bg-line" />
      </main>
    )
  }

  if (isError) {
    return (
      <main className="grid min-h-dvh place-items-center p-6">
        <div className="w-full max-w-md">
          <Alert tone="error" title="Unable to load">
            {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </div>
      </main>
    )
  }

  return (
    <SiteProvider site={data}>
      <I18nProvider site={data}>
        <BrowserRouter>
        <Routes>
          <Route element={<FunnelLayout />}>
            <Route path="/" element={<StepAccount />} />
            <Route path="/identity" element={<StepIdentity />} />
            {/*
              Didit sends the user back here. The path is fixed and known to the
              API, which builds the callback from the site's configured host
              rather than from the request (findings #1, #13).
            */}
            <Route path="/verify/return" element={<VerifyReturn />} />
            {/* Post-KYC: confirm the name and address Didit read (§11). */}
            <Route path="/confirm" element={<ConfirmDetails />} />
            <Route path="/compliance" element={<StepCompliance />} />
            <Route path="/done" element={<StepDone />} />
          </Route>

          {/*
            Signing in and getting back in (§10). Same chrome, outside the
            funnel: these visitors already have an account, and the resume check
            the funnel layout runs would push them into a half-finished
            application that happens to be left in this browser.
          */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          {/* An unknown path is a mistyped or stale link, not an error worth a
              screen. Send them to the start; the resume check sorts out where
              they actually belong. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
        {!consent && <ConsentBanner onDecide={(a, m) => setConsent(writeConsent(a, m))} />}
      </I18nProvider>
    </SiteProvider>
  )
}
